// ─── Impostazioni → Chiusure ─────────────────────────────────────────────────
//
// 🗣️ Richiesta del committente dell'08/09/2026: *«devo poter inserire delle
// festività […] non devo lasciare dei buchi di giorni»*. Una chiusura sta SOPRA
// gli orari: vale qualunque griglia sia in vigore quel giorno.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { primoGiornoChiuso, ultimoGiornoChiuso, type Chiusura } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { CampoData } from '../../components/CampoData.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'chiusure';

/**
 * `2026-12-25` → l'istante della mezzanotte di quel giorno, con il fuso di chi
 * guarda.
 *
 * ⚠️ Il fuso è quello del BROWSER, e per la segreteria è quello del circolo. Sta
 * scritto perché è un'assunzione, non una certezza: una chiusura compilata da
 * un fuso diverso comincerebbe a un'ora diversa.
 */
function mezzanotte(giorno: string, piu = 0): string {
  const d = new Date(`${giorno}T00:00:00`);
  d.setDate(d.getDate() + piu);
  return d.toISOString();
}

/**
 * Due scritture dello stesso momento sono lo stesso momento.
 * `2026-12-25T00:00:00+01:00` e `2026-12-24T23:00:00.000Z` sono testi diversi e
 * istanti uguali: confrontarli come stringhe direbbe «è cambiato» a chi non ha
 * toccato niente.
 */
function stessoIstante(a: string, b: string): boolean {
  return new Date(a).getTime() === new Date(b).getTime();
}

/** `2026-12-25` → `25/12/2026`, senza ripassare da un fuso. */
function italiano(giorno: string): string {
  return `${giorno.slice(8, 10)}/${giorno.slice(5, 7)}/${giorno.slice(0, 4)}`;
}

export function Chiusure(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: Chiusura[] }>('GET', '/settings/closures'),
  });

  if (elenco.isLoading) return <Attesa cosa="le chiusure" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];

  return (
    <section>
      <header className="testata">
        <h2>Chiusure</h2>
        <p className="tenue">
          I giorni in cui il circolo è chiuso. Valgono su tutti i campi, e coprono qualunque orario sia in
          vigore.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {items.length === 0 ? (
        <Vuoto titolo="Nessuna chiusura" perché="Il circolo risulta aperto tutti i giorni dell’anno." />
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Dal</th>
                <th>Al</th>
                <th>Motivo</th>
                <th>Ricorre</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <Riga key={c.id} chiusura={c} modificabile={modificabile} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modificabile && <Nuova />}
    </section>
  );
}

function Riga({ chiusura, modificabile }: { chiusura: Chiusura; modificabile: boolean }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  /**
   * ⚠️ Il pannello si apre in una RIGA SUA sotto, non dentro una cella: un
   * modulo dentro un `<td>` allargherebbe la colonna e sfascerebbe la tabella.
   * È la stessa scelta già fatta per i campi.
   */
  const [aperto, setAperto] = useState(false);
  const elimina = useMutation({
    meta: { fatto: 'chiusura tolta' },
    mutationFn: () => chiama('DELETE', `/settings/closures/${chiusura.id}`),
    onSuccess: rileggi,
  });

  return (
    <>
      <tr>
        <td>{italiano(primoGiornoChiuso(chiusura))}</td>
        <td>{italiano(ultimoGiornoChiuso(chiusura))}</td>
        <td>{chiusura.reason ?? '—'}</td>
        <td>{chiusura.everyYear ? 'ogni anno' : 'una volta'}</td>
        <td className="azioni">
          {modificabile && (
            <>
              <button
                type="button"
                className="piatto"
                aria-expanded={aperto}
                onClick={() => setAperto((v) => !v)}
              >
                Modifica
              </button>
              {/* 🚨 Cancellare non è correggere: una chiusura cancellata lascia
                  il circolo APERTO in quei giorni finché non se ne scrive
                  un'altra, e nel mezzo qualcuno può prenotare. */}
              <button
                type="button"
                className="piatto pericolo"
                disabled={elimina.isPending}
                onClick={() => elimina.mutate()}
              >
                Elimina
              </button>
            </>
          )}
          <Guasto errore={elimina.error} />
        </td>
      </tr>
      {modificabile && aperto && (
        <tr className="riga-modifica">
          <td colSpan={5}>
            <Modifica chiusura={chiusura} chiudi={() => setAperto(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Cambiare una chiusura già scritta.
 *
 * ⭐ Si manda SOLO quello che è cambiato davvero: `modificaChiusuraSchema` dice
 * «non toccarlo» per ciò che non arriva, quindi rispedire tutto riscriverebbe
 * anche i valori che nessuno ha guardato.
 *
 * ⚠️ Le due date si confrontano per ISTANTE e non per testo: il server le
 * rimanda con il suo fuso (`+01:00`), il modulo le ricostruisce in `Z`, e due
 * scritture diverse dello stesso momento sembrerebbero una modifica.
 */
function Modifica({ chiusura, chiudi }: { chiusura: Chiusura; chiudi: () => void }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [dal, setDal] = useState(primoGiornoChiuso(chiusura));
  const [al, setAl] = useState(ultimoGiornoChiuso(chiusura));
  const [motivo, setMotivo] = useState(chiusura.reason ?? '');
  const [ogniAnno, setOgniAnno] = useState(chiusura.everyYear);

  const inizio = dal === '' ? null : mezzanotte(dal);
  const fine = dal === '' ? null : mezzanotte(al === '' ? dal : al, 1);
  const motivoScelto = motivo.trim() === '' ? null : motivo.trim();

  const cambiato: Record<string, unknown> = {};
  if (inizio !== null && !stessoIstante(inizio, chiusura.startsAt)) cambiato.startsAt = inizio;
  if (fine !== null && !stessoIstante(fine, chiusura.endsAt)) cambiato.endsAt = fine;
  if (motivoScelto !== chiusura.reason) cambiato.reason = motivoScelto;
  if (ogniAnno !== chiusura.everyYear) cambiato.everyYear = ogniAnno;
  const niente = Object.keys(cambiato).length === 0;

  const salva = useMutation({
    meta: { fatto: 'chiusura salvata' },
    mutationFn: () => chiama('PATCH', `/settings/closures/${chiusura.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (!niente) salva.mutate();
      }}
    >
      <div className="riga-campi">
        <CampoData etichetta="Dal" required value={dal} onChange={setDal} />
        <CampoData etichetta="Al (compreso)" value={al} onChange={setAl} />
        <Campo etichetta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </div>
      <label className="campo orizzontale">
        <input type="checkbox" checked={ogniAnno} onChange={(e) => setOgniAnno(e.target.checked)} />
        <span>Torna ogni anno (Natale, Ferragosto…)</span>
      </label>
      {ogniAnno && (
        <p className="tenue piccolo">
          Una ricorrente non può scavalcare il capodanno: per il 28/12 → 02/01 servono due righe.
        </p>
      )}
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending || niente}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Annulla
        </button>
      </div>
    </form>
  );
}

function Nuova(): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ogniAnno, setOgniAnno] = useState(false);

  const crea = useMutation({
    meta: { fatto: 'chiusura aggiunta' },
    mutationFn: () =>
      chiama('POST', '/settings/closures', {
        // ⚖️ L'ultimo giorno è chiuso PER INTERO: la fine è la mezzanotte del
        // giorno dopo, non quella del giorno stesso — che lo lascerebbe aperto.
        startsAt: mezzanotte(dal),
        endsAt: mezzanotte(al === '' ? dal : al, 1),
        reason: motivo.trim() === '' ? null : motivo,
        everyYear: ogniAnno,
      }),
    onSuccess: () => {
      setDal('');
      setAl('');
      setMotivo('');
      rileggi();
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Aggiungi una chiusura</h3>
      <p className="tenue piccolo">
        Di giorno intero. Per una domenica in cui si chiude alle 18 non serve una mezza chiusura: serve una
        fascia più corta.
      </p>
      <CampoData etichetta="Dal" required value={dal} onChange={setDal} />
      <CampoData etichetta="Al (vuoto = un giorno solo)" value={al} onChange={setAl} />
      <Campo etichetta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      <label className="campo orizzontale">
        <input type="checkbox" checked={ogniAnno} onChange={(e) => setOgniAnno(e.target.checked)} />
        <span>Torna ogni anno (Natale, Ferragosto…)</span>
      </label>
      {ogniAnno && (
        <p className="tenue piccolo">
          Una ricorrente non può scavalcare il capodanno: per il 28/12 → 02/01 servono due righe.
        </p>
      )}
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending ? 'Un attimo…' : 'Aggiungi'}
      </button>
    </form>
  );
}
